import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { errorMessage, tokensApi, type ApiTokenInfo } from '../../lib/api.ts'
import { relativeTime } from '../../lib/relativeTime.ts'
import { EmptyState, ErrorState, Skeleton } from '../../ui/primitives.tsx'
import styles from './settings.module.css'

/**
 * API / MCP tokens (ui-spec §5.5, PRD §4.5).
 *
 * The freshly created value lives in this component's state and nowhere else — not in the query
 * cache, not in localStorage. It is deliberately impossible to get it back after a reload, because
 * the server kept only a sha256 and pretending otherwise would be a lie about the security model.
 */
export function TokenSection(): React.JSX.Element {
  const queryClient = useQueryClient()
  const [label, setLabel] = useState('')
  const [adding, setAdding] = useState(false)
  const [revealed, setRevealed] = useState<{ label: string; token: string } | null>(null)
  const [copied, setCopied] = useState(false)
  const [confirming, setConfirming] = useState<number | null>(null)

  const tokens = useQuery({
    queryKey: ['tokens'],
    queryFn: async () => (await tokensApi.list()).tokens,
  })

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['tokens'] })
  }

  const create = useMutation({
    mutationFn: (name: string) => tokensApi.create(name),
    onSuccess: ({ token, tokenInfo }) => {
      setRevealed({ label: tokenInfo.label, token })
      setCopied(false)
      setLabel('')
      setAdding(false)
      invalidate()
    },
  })

  const revoke = useMutation({
    mutationFn: (id: number) => tokensApi.revoke(id),
    onSuccess: () => {
      setConfirming(null)
      invalidate()
    },
  })

  /**
   * `navigator.clipboard` is undefined outside a secure context — over plain http on the LAN the
   * old version threw into a swallowed rejection, so the button did nothing and said nothing.
   * The fallback is to leave the value selected so Ctrl+C still works.
   */
  const [copyFailed, setCopyFailed] = useState(false)
  const copy = async (): Promise<void> => {
    if (!revealed) return
    try {
      await navigator.clipboard.writeText(revealed.token)
      setCopied(true)
      setCopyFailed(false)
    } catch {
      setCopyFailed(true)
    }
  }

  const submit = (): void => {
    const name = label.trim()
    if (name) create.mutate(name)
  }

  const list = tokens.data ?? []

  return (
    <section className={styles.panel}>
      <div className={styles.panelHead}>
        <h2 className="section-label">Tokeny API / MCP</h2>
        {!adding && (
          <button type="button" className={styles.primary} onClick={() => setAdding(true)}>
            Nowy token
          </button>
        )}
      </div>

      {adding && (
        <div className={styles.newTokenRow}>
          <input
            className={styles.field}
            // Pole pojawia się dopiero po kliknięciu „Nowy token" — focus idzie za akcją.
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            placeholder="Etykieta, np. claude-code-laptop"
            aria-label="Etykieta tokena"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit()
              if (e.key === 'Escape') setAdding(false)
            }}
          />
          <button
            type="button"
            className={styles.primary}
            disabled={!label.trim() || create.isPending}
            onClick={submit}
          >
            Utwórz
          </button>
          <button type="button" className={styles.ghost} onClick={() => setAdding(false)}>
            Anuluj
          </button>
        </div>
      )}

      {revealed && (
        <div className={styles.reveal}>
          <p className={styles.revealHead}>Token „{revealed.label}" — widoczny tylko teraz</p>
          <div className={styles.revealRow}>
            <code className={styles.revealValue}>{revealed.token}</code>
            <button type="button" className={styles.ghost} onClick={() => void copy()}>
              {copied ? 'Skopiowano' : 'Kopiuj'}
            </button>
          </div>
          {copyFailed && (
            <ErrorState text="Przeglądarka nie dała dostępu do schowka — zaznacz wartość i skopiuj ręcznie." />
          )}
          <p className={styles.revealNote}>
            Po zamknięciu tej karty nie zobaczysz go ponownie. W bazie trzymamy tylko sha256.
          </p>
        </div>
      )}

      {/* Nieudana mutacja cofała się w ciszy: lista odświeżała się bez nowego tokena i nic nie
          mówiło, dlaczego. */}
      {create.isError && (
        <ErrorState
          text={`Nie udało się utworzyć tokena. ${errorMessage(create.error)}`}
          onRetry={() => create.mutate(label.trim() || 'token')}
        />
      )}
      {revoke.isError && <ErrorState text="Nie udało się unieważnić tokena." />}

      {tokens.isError ? (
        <ErrorState
          text="Nie udało się wczytać listy tokenów."
          onRetry={() => void tokens.refetch()}
        />
      ) : tokens.isPending ? (
        <>
          <Skeleton height={34} delay={0} />
          <Skeleton height={34} delay={200} />
        </>
      ) : list.length > 0 ? (
        <div className={styles.tokenList}>
          <div className={`${styles.tokenRow} ${styles.tokenHead}`}>
            <span>Etykieta</span>
            <span>Utworzony</span>
            <span>Ostatnio użyty</span>
            <span />
          </div>
          {list.map((token) => (
            <TokenRow
              key={token.id}
              token={token}
              confirming={confirming === token.id}
              onAskRevoke={() => setConfirming(token.id)}
              onCancel={() => setConfirming(null)}
              onRevoke={() => revoke.mutate(token.id)}
              revoking={revoke.isPending}
            />
          ))}
        </div>
      ) : (
        // Bez `!tokens.isPending` — ładowanie ma teraz własną gałąź wyżej.
        <EmptyState text="Nie ma jeszcze żadnego tokena" variant="plain" />
      )}
    </section>
  )
}

function TokenRow({
  token,
  confirming,
  onAskRevoke,
  onCancel,
  onRevoke,
  revoking,
}: {
  token: ApiTokenInfo
  confirming: boolean
  onAskRevoke: () => void
  onCancel: () => void
  onRevoke: () => void
  revoking: boolean
}): React.JSX.Element {
  return (
    <div className={styles.tokenRow}>
      <span className={styles.tokenLabel}>{token.label}</span>
      <span className={styles.tokenTime}>{relativeTime(token.createdAt)}</span>
      <span className={token.lastUsedAt ? styles.tokenTime : styles.tokenNever}>
        {token.lastUsedAt ? relativeTime(token.lastUsedAt) : 'nigdy'}
      </span>
      {confirming ? (
        <span className={styles.confirm}>
          {/* disabled w locie: bez tego dwa kliknięcia wysyłają dwa DELETE, a drugi wraca 404. */}
          <button type="button" className={styles.danger} disabled={revoking} onClick={onRevoke}>
            Na pewno
          </button>
          <button type="button" className={styles.ghost} onClick={onCancel}>
            Nie
          </button>
        </span>
      ) : (
        <button type="button" className={styles.danger} onClick={onAskRevoke}>
          Unieważnij
        </button>
      )}
    </div>
  )
}
